import React, { useState, useEffect } from 'react';
import { Clock, AlertCircle, ThumbsUp, ThumbsDown, Bookmark, BookmarkCheck, Share2, Check } from 'lucide-react';
import ToolDetailCard from './ToolDetailCard';
import { Language, t } from '../utils/i18n';
import { isFavorite, addFavorite, removeFavorite } from '../utils/storage';
import { submitFeedback, getFeedbackStats } from '../api/feedbackApi';

interface SearchResultsProps {
  results: any;
  query: string;
  lang: Language;
  onSearch?: (query: string) => void; // 新增：搜索回调
}

export default function SearchResults({ results, query, lang, onSearch }: SearchResultsProps) {
  const [expandedIndex, setExpandedIndex] = useState(0);

  // 相关工具的反馈和收藏状态
  const [relatedToolsFeedback, setRelatedToolsFeedback] = useState<Record<string, { up: number; down: number; userFeedback: 'up' | 'down' | null }>>({});
  const [relatedToolsFavorites, setRelatedToolsFavorites] = useState<Record<string, boolean>>({});
  const [copiedShareTool, setCopiedShareTool] = useState<string | null>(null);
  const [preloadedTools, setPreloadedTools] = useState<Set<string>>(new Set());

  // 预加载相关工具的数据
  const preloadToolData = async (toolName: string) => {
    if (preloadedTools.has(toolName)) return;

    try {
      console.log('🔥 预加载相关工具:', toolName);
      const response = await fetch('/api/warmup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ queries: [toolName] })
      });

      if (response.ok) {
        setPreloadedTools(prev => new Set([...prev, toolName]));
        console.log('✅ 预加载成功:', toolName);
      }
    } catch (error) {
      console.log('⚠️ 预加载失败:', toolName, error);
    }
  };

  // 处理相关工具卡片点击
  const handleRelatedToolClick = (toolName: string) => {
    if (onSearch) {
      onSearch(toolName);
    }
  };

  // 加载相关工具的反馈统计
  useEffect(() => {
    if (results.relatedTools && results.relatedTools.length > 0 && query) {
      results.relatedTools.forEach(async (tool: any) => {
        const stats = await getFeedbackStats(tool.name, query);
        if (stats) {
          setRelatedToolsFeedback(prev => ({
            ...prev,
            [tool.name]: {
              up: stats.up_count,
              down: stats.down_count,
              userFeedback: null
            }
          }));
        }

        // 加载收藏状态
        setRelatedToolsFavorites(prev => ({
          ...prev,
          [tool.name]: isFavorite(tool.name)
        }));

        // 预加载相关工具数据（延迟执行，避免阻塞）
        setTimeout(() => {
          preloadToolData(tool.name);
        }, 500);
      });
    }
  }, [results.relatedTools, query]);

  // 处理相关工具的点赞/点踩
  const handleRelatedToolFeedback = (toolName: string, type: 'up' | 'down') => {
    if (!query) return;

    const current = relatedToolsFeedback[toolName] || { up: 0, down: 0, userFeedback: null };

    // 如果已经点过相同的，直接返回
    if (current.userFeedback === type) return;

    // 乐观更新
    const oldFeedback = current.userFeedback;
    let newStats = { ...current };

    if (oldFeedback === 'up') {
      newStats.up--;
    } else if (oldFeedback === 'down') {
      newStats.down--;
    }

    if (type === 'up') {
      newStats.up++;
    } else {
      newStats.down++;
    }

    newStats.userFeedback = type;

    setRelatedToolsFeedback(prev => ({
      ...prev,
      [toolName]: newStats
    }));

    // 异步提交
    submitFeedback(toolName, query, type, lang).then(result => {
      if (!result.success) {
        // 回滚
        setRelatedToolsFeedback(prev => ({
          ...prev,
          [toolName]: current
        }));
      }
    }).catch(() => {
      setRelatedToolsFeedback(prev => ({
        ...prev,
        [toolName]: current
      }));
    });
  };

  // 处理相关工具的收藏切换
  const handleRelatedToolFavorite = (e: React.MouseEvent, toolName: string) => {
    e.preventDefault();
    e.stopPropagation();

    const isFav = isFavorite(toolName);
    if (isFav) {
      removeFavorite(toolName);
    } else {
      addFavorite(toolName);
    }

    setRelatedToolsFavorites(prev => ({
      ...prev,
      [toolName]: !isFav
    }));
  };

  // 处理相关工具的分享
  const handleRelatedToolShare = (e: React.MouseEvent, toolName: string) => {
    e.preventDefault();
    e.stopPropagation();

    const shareUrl = `${window.location.origin}?q=${encodeURIComponent(toolName)}`;

    if (navigator.share) {
      navigator.share({
        title: toolName,
        url: shareUrl
      }).catch(() => {
        fallbackShareTool(toolName);
      });
    } else {
      fallbackShareTool(toolName);
    }
  };

  const fallbackShareTool = (toolName: string) => {
    const shareUrl = `${window.location.origin}?q=${encodeURIComponent(toolName)}`;
    navigator.clipboard.writeText(shareUrl);
    setCopiedShareTool(toolName);
    setTimeout(() => setCopiedShareTool(null), 2000);
  };

  return (
    <div className="space-y-6">
      {/* 拼写纠错提示 */}
      {results.correctedQuery && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <div className="flex items-center gap-2 text-sm">
            <AlertCircle className="w-4 h-4 text-[#165DFF]" />
            <span className="text-gray-700 dark:text-gray-300">
              {lang === 'zh' ? '你是不是想找：' : 'Did you mean: '}
              <span className="font-semibold text-[#165DFF] ml-2">
                {results.correctedQuery}
              </span>
            </span>
          </div>
        </div>
      )}

      {/* 结果统计 */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
            <Clock className="w-4 h-4" />
            <span>
              {t('resultsCount', lang)} <span className="font-semibold text-gray-800 dark:text-gray-200">{results.resultCount}</span> {t('searchTime', lang)}
              <span className="text-gray-400 ml-2">({results.searchTime})</span>
            </span>
          </div>

          {/* 缓存来源提示 */}
          {(results.fromCache !== undefined || results.debugInfo) && (
            <span className={`
              inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium
              ${results.fromCache
                ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800'
                : 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 border border-orange-200 dark:border-orange-800'
              }
            `}>
              {results.fromCache ? (
                <>
                  <span className="w-2 h-2 rounded-full bg-green-500 dark:bg-green-400 animate-pulse"></span>
                  {lang === 'zh' ? '服务器缓存' : 'Server Cache'}
                </>
              ) : (
                <>
                  <span className="w-2 h-2 rounded-full bg-orange-500 dark:bg-orange-400"></span>
                  {lang === 'zh' ? 'AI 实时生成' : 'AI Generated'}
                </>
              )}
            </span>
          )}
        </div>
        <div className="text-sm text-gray-500 dark:text-gray-400">
          {t('searchIntent', lang)}：{results.searchIntent}
        </div>
      </div>

      {/* 结果区域 */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* 主结果区 - 双语卡片 */}
        <div className="lg:col-span-3 space-y-6">
          {/* 根据 API 返回的数据结构判断是否为双语版本 */}
          {results.zh && results.en && (lang === 'zh' ? results.zh.results : results.en.results) ? (
            // 新版双语数据：根据语言决定显示哪个版本
            (lang === 'zh' ? results.zh.results : results.en.results).map((tool: any, index: number) => (
              <ToolDetailCard
                key={`bilingual-${index}`}
                tool={tool}
                isExpanded={expandedIndex === index}
                onToggle={() => setExpandedIndex(expandedIndex === index ? -1 : index)}
                query={query}
                lang={lang}
              />
            ))
          ) : results.results && results.results.length > 0 ? (
            // 旧版单语数据：保持原样
            results.results.map((tool: any, index: number) => (
              <ToolDetailCard
                key={index}
                tool={tool}
                isExpanded={expandedIndex === index}
                onToggle={() => setExpandedIndex(expandedIndex === index ? -1 : index)}
                query={query}
                lang={lang}
              />
            ))
          ) : null}
        </div>

        {/* 侧边栏 */}
        <div className="lg:col-span-1 space-y-6">
          {/* 工具热点 */}
          <div className="bg-white dark:bg-[#2D2D3F] rounded-xl p-5 shadow-sm border border-gray-100 dark:border-gray-700">
            <h3 className="font-semibold text-gray-800 dark:text-gray-100 mb-4 flex items-center gap-2">
              <span>🔥</span>
              <span>工具热点</span>
            </h3>
            <div className="space-y-3">
              <a
                href="https://github.com/trending"
                target="_blank"
                rel="noopener noreferrer"
                className="block group"
              >
                <div className="text-sm text-gray-700 dark:text-gray-300 group-hover:text-[#165DFF] dark:group-hover:text-[#165DFF] transition-colors line-clamp-2">
                  GitHub Trending - 今日热门开源项目
                </div>
                <div className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                  github.com
                </div>
              </a>
              <a
                href="https://techcrunch.com/category/startups/"
                target="_blank"
                rel="noopener noreferrer"
                className="block group"
              >
                <div className="text-sm text-gray-700 dark:text-gray-300 group-hover:text-[#165DFF] dark:group-hover:text-[#165DFF] transition-colors line-clamp-2">
                  TechCrunch - 创业与技术动态
                </div>
                <div className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                  techcrunch.com
                </div>
              </a>
              <a
                href="https://www.infoq.cn/topic/open-source"
                target="_blank"
                rel="noopener noreferrer"
                className="block group"
              >
                <div className="text-sm text-gray-700 dark:text-gray-300 group-hover:text-[#165DFF] dark:group-hover:text-[#165DFF] transition-colors line-clamp-2">
                  InfoQ - 开源技术深度解读
                </div>
                <div className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                  infoq.cn
                </div>
              </a>
              <a
                href="https://www.oschina.net/news"
                target="_blank"
                rel="noopener noreferrer"
                className="block group"
              >
                <div className="text-sm text-gray-700 dark:text-gray-300 group-hover:text-[#165DFF] dark:group-hover:text-[#165DFF] transition-colors line-clamp-2">
                  开源中国 - 开源资讯快报
                </div>
                <div className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                  oschina.net
                </div>
              </a>
              <a
                href="https://juejin.cn/tag/前端"
                target="_blank"
                rel="noopener noreferrer"
                className="block group"
              >
                <div className="text-sm text-gray-700 dark:text-gray-300 group-hover:text-[#165DFF] dark:group-hover:text-[#165DFF] transition-colors line-clamp-2">
                  掘金 - 前端技术精选
                </div>
                <div className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                  juejin.cn
                </div>
              </a>
            </div>
          </div>

          {/* 相关推荐 */}
          {results.relatedTools && results.relatedTools.length > 0 && (
            <div className="bg-white dark:bg-[#2D2D3F] rounded-xl p-5 shadow-sm border border-gray-100 dark:border-gray-700">
              <h3 className="font-semibold text-gray-800 dark:text-gray-100 mb-4">
                相关工具
              </h3>
              <div className="space-y-3">
                {results.relatedTools.map((tool: any, index: number) => {
                  const feedback = relatedToolsFeedback[tool.name] || { up: 0, down: 0, userFeedback: null };
                  const isFav = relatedToolsFavorites[tool.name] || false;
                  const isPreloaded = preloadedTools.has(tool.name);

                  return (
                    <div
                      key={index}
                      className="pb-3 border-b border-gray-100 dark:border-gray-700 last:border-0 group cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 rounded px-2 py-2 -mx-2 -my-2 transition-colors"
                      onClick={() => handleRelatedToolClick(tool.name)}
                      title={isPreloaded ? '已预加载，点击快速查看' : '点击查看详情'}
                    >
                      <div className="flex items-start justify-between gap-2">
                        {/* 工具信息 */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2 mb-1">
                            <div className="font-medium text-sm text-gray-800 dark:text-gray-200 group-hover:text-[#165DFF] transition-colors">
                              {tool.name}
                              {isPreloaded && (
                                <span className="ml-2 text-xs text-green-500">⚡</span>
                              )}
                            </div>
                            {/* 收藏和分享 */}
                            <div className="flex items-center gap-1 flex-shrink-0">
                              {/* 收藏 */}
                              <button
                                onClick={(e) => handleRelatedToolFavorite(e, tool.name)}
                                className={`p-1.5 rounded transition-colors ${
                                  isFav ? 'text-[#165DFF]' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
                                }`}
                                title={isFav ? '取消收藏' : '收藏'}
                              >
                                {isFav ? (
                                  <BookmarkCheck className="w-3.5 h-3.5" />
                                ) : (
                                  <Bookmark className="w-3.5 h-3.5" />
                                )}
                              </button>
                              {/* 分享 */}
                              <button
                                onClick={(e) => handleRelatedToolShare(e, tool.name)}
                                className="p-1.5 rounded transition-colors text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                title="分享"
                              >
                                {copiedShareTool === tool.name ? (
                                  <Check className="w-3.5 h-3.5 text-green-500" />
                                ) : (
                                  <Share2 className="w-3.5 h-3.5" />
                                )}
                              </button>
                            </div>
                          </div>
                          {/* 点赞/点踩 */}
                          <div className="flex items-center gap-2 mt-2 mb-2">
                            <div className="flex items-center gap-0.5 px-1.5 py-0.5 bg-gray-50 dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRelatedToolFeedback(tool.name, 'up');
                                }}
                                className={`p-1 rounded transition-colors ${
                                  feedback.userFeedback === 'up'
                                    ? 'text-green-600 dark:text-green-400'
                                    : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
                                }`}
                                title="这个推荐有用"
                              >
                                <ThumbsUp className="w-3.5 h-3.5" />
                              </button>
                              <span className="text-xs text-gray-500 dark:text-gray-400 min-w-[12px] text-center">
                                {feedback.up > 0 ? feedback.up : ''}
                              </span>
                              <div className="w-px h-3 bg-gray-300 dark:bg-gray-600" />
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRelatedToolFeedback(tool.name, 'down');
                                }}
                                className={`p-1 rounded transition-colors ${
                                  feedback.userFeedback === 'down'
                                    ? 'text-red-600 dark:text-red-400'
                                    : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
                                }`}
                                title="这个推荐没用"
                              >
                                <ThumbsDown className="w-3.5 h-3.5" />
                              </button>
                              <span className="text-xs text-gray-500 dark:text-gray-400 min-w-[12px] text-center">
                                {feedback.down > 0 ? feedback.down : ''}
                              </span>
                            </div>
                          </div>
                          {/* 关系标签 */}
                          {tool.relationTag && (
                            <span className={`inline-block text-xs px-2 py-0.5 rounded mb-1 ${
                              tool.relationTag === '搭配使用'
                                ? 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400'
                                : tool.relationTag === '替代工具'
                                ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                                : 'bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400'
                            }`}>
                              {tool.relationTag}
                            </span>
                          )}
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {tool.category}
                          </div>
                          <div className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                            {tool.reason}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* 侧边栏广告 */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-700 rounded-xl p-5 border-2 border-[#165DFF] border-dashed">
            <div className="text-xs text-gray-500 dark:text-gray-400 text-center mb-2">
              广告
            </div>
            <div className="text-center">
              <h4 className="font-semibold text-gray-800 dark:text-gray-200 mb-2">
                {query} 实战教程
              </h4>
              <p className="text-xs text-gray-600 dark:text-gray-300 mb-3">
                从入门到精通<br />限时免费试听
              </p>
              <button className="w-full px-4 py-2 bg-[#165DFF] text-white text-sm rounded-lg hover:bg-[#0E4FD0] transition-colors">
                立即学习
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}